/**
 * slotKind — which of the two very different things an "open" timeSlot is.
 *
 * `status: 'open'` means two unrelated things depending on the doc's shape, and
 * the Slot Editor used to render both identically:
 *
 *   - a WEEKLY slot (one doc + an `exceptions` map, no `specificDate`) that is
 *     open is a permanent time with no student: the /register pool new families
 *     book a whole school year into;
 *   - a SINGLE-DATE slot (`specificDate`, usually `isMakeup: true`) that is open
 *     is ONE lesson on ONE day that credit-holders can book. A family cancel
 *     outside 24h publishes one of these (functions/makeupCredits.js).
 *
 * Blocking the second shuts one date; blocking the first takes a weekly time
 * out of registration. Anthony could not tell them apart on 2026-10-29 while
 * trying to close two cancelled Lakeville lessons for the next day, and asked
 * whether blocking would close the times "permanently". The single-date docs
 * carry no `dayOfWeek` either, so their Day cell rendered blank.
 *
 * `specificDate` is the discriminator, not `isRecurring` (older docs may lack
 * the flag) and not `isMakeup` (an admin can create a plain one-date slot).
 */

export const isSingleDate = (slot) => !!slot?.specificDate;

/** 'weekly' | 'makeup' | 'oneDate' */
export function slotKind(slot) {
	if (!isSingleDate(slot)) return 'weekly';
	return slot.isMakeup ? 'makeup' : 'oneDate';
}

export const isOpenForRegistration = (slot) => slot?.status === 'open' && !isSingleDate(slot);
export const isOpenForMakeup = (slot) => slot?.status === 'open' && isSingleDate(slot);

// "2026-10-30" -> local Date. Never `new Date(str)`: that parses as UTC
// midnight, which is the evening before in every US timezone (CLAUDE.md).
function parseDateKey(key) {
	const [y, m, d] = String(key).split('-').map(Number);
	if (!y || !m || !d) return null;
	return new Date(y, m - 1, d);
}

/** Weekday 0-6 for any slot: the field on a weekly slot, derived from the date otherwise. */
export function slotWeekday(slot) {
	if (!isSingleDate(slot)) return slot?.dayOfWeek;
	const date = parseDateKey(slot.specificDate);
	return date ? date.getDay() : undefined;
}

/** "Fri, Oct 30" for a single-date slot; null for a weekly one. */
export function formatSlotDate(slot) {
	if (!isSingleDate(slot)) return null;
	const date = parseDateKey(slot.specificDate);
	if (!date) return slot.specificDate;
	return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function localDateKey(now) {
	const pad = (n) => String(n).padStart(2, '0');
	return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** A single-date slot whose day is before today. Weekly slots are never past. */
export function isPastDate(slot, now = new Date()) {
	return isSingleDate(slot) && slot.specificDate < localDateKey(now);
}
